(ns graph-ui.web.nav
  "Where the reader has been, and the two arrows that walk it — the
   `back-and-forward` part of `ikc/top-bar` (dsl/platform/top-bar.edn).

   WHY CLIENT-SIDE, like graph-ui.web.activity and graph-ui.web.busy: a trail is
   a fact about THIS tab, not about the graph. Two tabs on one island are at two
   different places, and one server-side trail could only be wrong for one of
   them. It also needs no new command, so it goes live with a bundle and a
   reload, and the shared bus keeps running.

   ONE ENTRY IS ONE PAGE ID. The trail is a vector plus an index, which is the
   only shape that gives forward as well as back: `back!` moves the index and
   leaves the entries alone, so the page you left is still ahead of you.

   RECORD! IS IDEMPOTENT AGAINST THE CURRENT ENTRY, and that is what makes the
   arrows work with no suppression flag anywhere. `back!` moves the index FIRST
   and then asks the server for that page, so the reply it causes arrives with a
   page id that already sits under the index — record! sees no change and does
   nothing. A reader who clicks the page they are already on is the same case.

   A FAILED WALK PUTS THE INDEX BACK. The index moves before the round trip,
   because the arrow must answer the click at once. If the server then refuses,
   the trail would say the reader is somewhere they never arrived, so the move
   is undone. An arrow must not report a place the reader never reached."
  (:require [reagent.core :as r]))

;; @feature F-graph-ui-v0-013-01-back-and-forward
;; @concept CONCEPT-graph-ui-v0-010-01-disabled-affordance
;; @adr ADR-graph-ui-v0-014-01-ui-building-blocks

;; Newest-last, with :index naming the entry the reader is on. -1 is "nowhere
;; yet", which is the state the arrows are grey in.
(defonce ^:private trail (r/atom {:entries [] :index -1}))

;; A trail is a convenience, not a journal (that is ADR-ikc-v3-035-01), so it is
;; capped. The OLDEST entry goes, and the index follows it down — dropping from
;; the front without moving the index would silently re-point every arrow.
(def ^:private cap 100)

(defn- push
  "Pure: the trail after a visit to `page-id`. Truncates whatever was ahead,
   which is what makes a new click from the middle of a trail lose the forward
   entries rather than interleave with them."
  [{:keys [entries index]} page-id]
  (let [kept (conj (subvec entries 0 (inc index)) page-id)
        n    (count kept)]
    (if (> n cap)
      {:entries (subvec kept (- n cap)) :index (dec cap)}
      {:entries kept :index (dec n)})))

(defn record!
  "Note that the reader arrived at `page-id`. Does nothing when that is already
   the current entry — see the ns docstring, this is load-bearing rather than a
   tidy-up. Called from graph-ui.web.bus-client on a successful :ui/select-page."
  [page-id]
  ;; ONE SPELLING OF AN ID. A reply records the command's :page-id, and an
  ;; event records its :target, which the server sends as a string. A uuid and
  ;; its string are not `=`, so both are kept as strings here.
  (when-let [page-id (some-> page-id str not-empty)]
    (swap! trail (fn [{:keys [entries index] :as st}]
                   (if (= page-id (get entries index))
                     st
                     (push st page-id))))))

(defn arrived!
  "Note the page that a :ui/selection-changed event of THIS tab's session
   announces. The caller checks the session, because only core knows the
   tab's session id.

   THE EVENT, NOT ONLY THE REPLY (fault report e776e38b). The reply path
   records a :ui/select-page alone. A daily note opens through
   :ui/goto-daily-note, and a new link through :ui/follow-wikilink. Neither is
   a :ui/select-page, so the trail did not hold the daily note, and Back from
   a link went one page too far. Every command that opens a page emits this
   event, so the trail now holds every page the reader reached.

   An outline node is not a page, and a deselection has no target. Neither is
   recorded."
  [{:keys [kind target]}]
  (when (= :page kind)
    (record! target)))

(defn can-back?
  "Reactive: is there an earlier page? Deref inside a reagent render and the
   arrow greys and ungreys by itself."
  []
  (pos? (:index @trail)))

(defn can-forward?
  "Reactive: is there a page ahead — one the reader walked back from?"
  []
  (let [{:keys [entries index]} @trail]
    (< index (dec (count entries)))))

(defn current
  "Reactive: the page the reader is on, or nil before the first visit. A phone
   dock reads it to go back to the reading pane when a new page opens."
  []
  (let [{:keys [entries index]} @trail]
    (get entries index)))

(defn depth
  "How many pages the trail holds. For a tooltip, and for the tests."
  []
  (count (:entries @trail)))

(defn- walk!
  "Move the index by `step` and ask the server for the page that lands under it.
   Puts the index back if the server refuses, so the arrows never claim a place
   the reader did not reach."
  [bus session-id step]
  (let [{:keys [entries index]} @trail
        to     (+ index step)
        target (get entries to)]
    (when (and bus session-id (some? target))
      (swap! trail assoc :index to)
      (js-invoke bus "dispatch"
                 {:type :ui/select-page :session-id session-id :page-id target}
                 (fn [result]
                   (when (:error result)
                     (swap! trail assoc :index index)))))))

(defn back!
  "The `<` arrow: one page earlier."
  [bus session-id]
  (when (can-back?) (walk! bus session-id -1)))

(defn forward!
  "The `>` arrow: one page later, where the reader has already been."
  [bus session-id]
  (when (can-forward?) (walk! bus session-id 1)))

(defn clear!
  "Empty the trail. For the tests, and for a session that starts again."
  []
  (reset! trail {:entries [] :index -1}))
